import React, { useState } from "react";
import { History, ChevronDown } from "lucide-react";
import { euro, fmtDateTime } from "../lib/format";
import { qtyWithUnit } from "../lib/units";

function RevisionEntry({ r, n, cur }) {
  const changes = Object.entries(r.quantities || {});
  const diff = r.total_before != null && r.total_after != null ? r.total_after - r.total_before : null;
  return (
    <li data-testid={`revision-entry-${n}`} className="relative pl-6 pb-5 last:pb-0 border-l border-slate-200 last:border-l-transparent">
      <span className="absolute -left-[7px] top-0.5 w-3.5 h-3.5 rounded-full bg-indigo-100 border-2 border-indigo-500" />
      <p className="text-sm font-semibold text-slate-900 flex flex-wrap items-center gap-2">Revisione {n}{r.client_name ? ` · richiesta da ${r.client_name}` : ""}
        {r.rejected && <span data-testid={`revision-rejected-${n}`} className="text-[11px] font-semibold bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">Respinta – quantità non disponibili</span>}
      </p>
      <p className="text-xs text-slate-500">
        Richiesta il {fmtDateTime(r.created_at)}{r.rejected ? ` · respinta il ${fmtDateTime(r.rejected_at)}` : r.resolved_at ? ` · rinviata il ${fmtDateTime(r.resolved_at)}` : ""}
      </p>
      {r.rejected && r.note && <p className="text-xs text-red-700 mt-1">Nota: {r.note}</p>}
      {changes.length > 0 && (
        <ul className="mt-2 space-y-1">
          {changes.map(([id, c]) => {
            const fin = r.final_quantities?.[id];
            return (
              <li key={id} className="text-sm text-slate-700 flex flex-wrap items-center gap-x-2">
                <span className="font-medium">{c.title || "Voce"}</span>
                <span className="font-mono text-slate-400">{qtyWithUnit(c.from, c.unit)} → {qtyWithUnit(c.to, c.unit)}</span>
                {!r.rejected && fin != null && fin !== c.to && <span className="text-xs text-amber-700">(confermata: {qtyWithUnit(fin, c.unit)})</span>}
              </li>
            );
          })}
        </ul>
      )}
      {r.message && <p className="mt-2 text-sm italic text-slate-600">"{r.message}"</p>}
      {r.total_before != null && (
        <p className="mt-2 text-xs text-slate-600" data-testid={`revision-totals-${n}`}>
          Totale: <span className="font-mono">{euro(r.total_before, cur)}</span>
          {r.total_after != null && !r.rejected && <> → <span className="font-mono font-semibold text-slate-900">{euro(r.total_after, cur)}</span>
            {diff !== 0 && <span className={`ml-1 font-mono ${diff > 0 ? "text-amber-700" : "text-emerald-700"}`}>({diff > 0 ? "+" : ""}{euro(diff, cur)})</span>}</>}
        </p>
      )}
    </li>
  );
}

export default function RevisionHistory({ history, cur = "EUR", collapsible = false, className = "" }) {
  const [open, setOpen] = useState(!collapsible);
  if (!history?.length) return null;
  const list = [...history].reverse();
  return (
    <div data-testid="revision-history" className={`rounded-xl border border-slate-200 bg-card shadow-sm ${className}`}>
      <button type="button" data-testid="revision-history-toggle" onClick={() => collapsible && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 p-5 text-left ${collapsible ? "cursor-pointer" : "cursor-default"}`}>
        <span className="font-semibold text-slate-900 flex items-center gap-2">
          <History style={{ width: 17, height: 17 }} className="text-primary" /> Storico revisioni
          <span className="text-xs font-semibold bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{history.length}</span>
        </span>
        {collapsible && <ChevronDown style={{ width: 18, height: 18 }} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && (
        <ol className="px-5 pb-5 pt-1 ml-1.5" data-testid="revision-history-list">
          {list.map((r, i) => <RevisionEntry key={r.id || i} r={r} n={history.length - i} cur={cur} />)}
        </ol>
      )}
    </div>
  );
}
